import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;  
  font-family: 'sans-serif';

`;

const BlueHeader = styled.div`
  background: #1e3a8a;
  color: white;
  padding: 20px;
  margin: -40px -32px 30px -32px;
  border-radius: 15px 15px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const LogoLetter = styled.div`
  width: 60px;
  height: 60px;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  
  &::after {
    content: attr(data-text);
    position: absolute;
    bottom: -15px;
    font-size: 8px;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const FormTitle = styled.h1`
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  text-transform: uppercase;
  margin: 30px 0;
  color: #2c3e50;
`;

const FormContent = styled.div`
  font-family:  sans-serif;
  line-height: 1.6;
  color: #333;
`;

const PermissionStatement = styled.div`
  margin: 20px 0;
  font-size: 1rem;
  line-height: 1.8;
`;

const Blank = styled.span`
  display: inline-block;
  min-width: 200px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const ShortBlank = styled.span`
  display: inline-block;
  min-width: 80px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin: 25px 0 15px 0;
  color: #2c3e50;
  font-style: italic;
`;

const CheckboxGroup = styled.div`
  margin: 20px 0;
  padding-left: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  min-width: 200px;
  
  input[type="checkbox"] {
    margin-right: 12px;
    transform: scale(1.2);
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 18px;
    height: 18px;
    border: 1px solid #333;
    border-radius: 3px;
    background-color: white;
    cursor: pointer;
    position: relative;
  }
  
  input[type="checkbox"]:checked {
    background-color: #000;
    border-color: #000;
  }
  
  input[type="checkbox"]:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 14px;
    font-weight: bold;
  }
  
  label {
    font-size: 1rem;
  }
`;

const OtherInput = styled.input`
  border: none;
  border-bottom: 1px solid #333;
  margin-left: 10px;
  padding: 3px 8px;
  min-width: 150px;
  background: transparent;
  
  &:focus {
    outline: none;
    border-bottom-color: #3498db;
  }
`;

const WaiverClause = styled.div`
  margin: 30px 0;
  font-size: 1rem;
  line-height: 1.7;
  text-align: justify;
`;

const RadioGroup = styled.div`
  margin: 20px 0;
`;

const RadioItem = styled.div`
  margin: 20px 0;
  
  input[type="radio"] {
    margin-right: 12px;
    transform: scale(1.2);
  }
  
  label {
    font-size: 1rem;
    line-height: 1.6;
    display: block;
  }
`;


const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 900px;
`;

const SignatureSection = styled.div`
  margin: 40px 0;
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0 0;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  color: #222;
`;

const SigValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 300px;
  display: inline-block;
`;

const SignatureImage = styled.img`
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border: none;
  background: transparent;
  border-bottom: 1px solid #333;
  margin-bottom: 18px;
  `;

const DateValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 150px;
  display: inline-block;
`;

const ParentSignatureNote = styled.div`
  font-size: 0.9rem;
  color: #666;
  
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  font-size: 0.8rem;
  color: #666;
`;

const FooterLogo = styled.div`
  width: 20px;
  height: 20px;
  background: #333;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const MediaReleaseForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container>
      <PrintHideStyle />
      <div ref={docRef} style={{ background: '#fff', color: '#222', padding: 0 }}>
        
      <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />
        
        <FormTitle>MEDIA RELEASE FORM</FormTitle>
        
        <FormContent>
          <PermissionStatement>
            I, <Blank>{student.firstName} {student.lastName}</Blank>, grant permission to <Blank>Chicago Community Learning Center</Blank>, hereinafter known as the "Media" to use my image (photographs and/or video) for use in Media publications including:
          </PermissionStatement>

          <SectionTitle>(Check All That Apply)</SectionTitle>
          
          <CheckboxGroup>
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="videos" 
                name="media_types" 
                value="Videos" 
                disabled 
                checked={student.media_videos === "Videos"}
              />
              <label htmlFor="videos">Videos</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="email-blasts" 
                name="media_types" 
                value="Email Blasts" 
                disabled 
                checked={student.media_email_blasts === "Email Blasts"}
              />
              <label htmlFor="email-blasts">Email Blasts</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="recruiting-brochures" 
                name="media_types" 
                value="Recruiting Brochures" 
                disabled 
                checked={student.media_recruiting_brochures === "Recruiting Brochures"}
              />
              <label htmlFor="recruiting-brochures">Recruiting Brochures</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="newsletters" 
                name="media_types" 
                value="Newsletters" 
                disabled 
                checked={student.media_newsletters === "Newsletters"}
              />
              <label htmlFor="newsletters">Newsletters</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="magazines" 
                name="media_types" 
                value="Magazines" 
                disabled 
                checked={student.media_magazines === "Magazines"}
              />
              <label htmlFor="magazines">Magazines</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="general-publications" 
                name="media_types" 
                value="General Publications" 
                disabled 
                checked={student.media_general_publications === "General Publications"}
              />
              <label htmlFor="general-publications">General Publications</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="website-affiliates" 
                name="media_types" 
                value="Website and/or Affiliates" 
                disabled 
                checked={student.media_website === "Website and/or Affiliates"}
              />
              <label htmlFor="website-affiliates">Website and/or Affiliates</label>
            </CheckboxItem>
            
            <CheckboxItem>
              <input 
                type="checkbox" 
                id="other" 
                name="media_types" 
                value="Other" 
                disabled 
                checked={student.media_other === "Other"}
              />
              <label htmlFor="other">Other: <OtherInput type="text" id="other-specify" placeholder="Specify other use" disabled /></label>
            </CheckboxItem>
          </CheckboxGroup>

          <WaiverClause>
            I hereby waive any right to inspect or approve the finished photographs or electronic matter that may be used in conjunction with them now or in the future, whether that use is known to me or unknown, and I waive any right to royalties or other compensation arising from or related to the use of the image.
          </WaiverClause>

          <SectionTitle>Please initial the paragraph below which is applicable to your present situation:</SectionTitle>
          
          <RadioGroup>
            <RadioItem>
              <ShortBlank>{student.firstName ? student.firstName.charAt(0) : ''}{student.lastName ? student.lastName.charAt(0) : ''}</ShortBlank> I am 20 years of age or older and I am competent to contract in my own name. I have read this release before signing below, and I fully understand the contents, meaning and impact of this release. I understand that I am free to address any specific questions regarding this release by submitting those questions in writing prior to signing, and I agree that my failure to do so will be interpreted as a free and knowledgeable acceptance of the terms of this release.
            </RadioItem>
            
            <RadioItem>
              <ShortBlank>{student.firstName ? student.firstName.charAt(0) : ''}{student.lastName ? student.lastName.charAt(0) : ''}</ShortBlank> I am the parent or legal guardian of the below named child. I have read this release before signing below, and I fully understand the contents, meaning and impact of this release. I understand that I am free to address any specific questions regarding this release by submitting those questions in writing prior to signing, and I agree that my failure to do so will be interpreted as a free and knowledgeable acceptance of the terms of this release.
            </RadioItem>
          </RadioGroup>

          <SignatureSection>
            <SignatureRow>
              <SigLabel>Signature:</SigLabel>
              {(() => {
                const sigData = student.student_ack_signature_data;
                
                // Check if it's a valid data URL (must have content after data:)
                if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }
                
                // Check if it's SVG markup
                if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: sigData }}
                      style={{ maxHeight: 40, maxWidth: 200 }}
                    />
                  );
                }
                
                // Check if it's a base64 string (without data URL prefix)
                if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                  return (
                    <SignatureImage 
                      src={`data:image/png;base64,${sigData}`} 
                      alt="Student Signature" 
                    />
                  );
                }
                
                // Check if it's a file path
                if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }
                
                // Return name if no valid signature data
                return <SigValue>{student.firstName} {student.lastName}</SigValue>;
              })()}
              <SigLabel style={{ marginLeft: '20px' }}>Date:</SigLabel>
              <DateValue>{dateStr}</DateValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Name (please print):</SigLabel>
              <SigValue>{student.firstName} {student.lastName}</SigValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Address:</SigLabel>
              <SigValue>
                {student.address || student.enroll_address || 'N/A'}
                {student.city || student.enroll_city ? `, ${student.city || student.enroll_city}` : ''}
                {student.state || student.enroll_state ? `, ${student.state || student.enroll_state}` : ''}
                {student.zipCode || student.enroll_zip ? ` ${student.zipCode || student.enroll_zip}` : ''}
                {student.country || student.enroll_country ? `, ${student.country || student.enroll_country}` : ''}
              </SigValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Signature of parent or legal guardian:</SigLabel>
              {(() => {
                const parentSigData = student.parent_signature_data || student.guardian_signature_data;
                
                // Check if it's a valid data URL (must have content after data:)
                if (typeof parentSigData === 'string' && parentSigData.startsWith('data:') && parentSigData.length > 6) {
                  return (
                    <SignatureImage src={parentSigData} alt="Parent/Guardian Signature" />
                  );
                }
                
                // Check if it's SVG markup
                if (typeof parentSigData === 'string' && parentSigData.trim().startsWith('<svg')) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: parentSigData }}
                      style={{ maxHeight: 40, maxWidth: 200 }}
                    />
                  );
                }
                
                // Check if it's a base64 string (without data URL prefix)
                if (typeof parentSigData === 'string' && parentSigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(parentSigData)) {
                  return (
                    <SignatureImage 
                      src={`data:image/png;base64,${parentSigData}`} 
                      alt="Parent/Guardian Signature" 
                    />
                  );
                }
                
                // Check if it's a file path
                if (typeof parentSigData === 'string' && (parentSigData.includes('.png') || parentSigData.includes('.jpg') || parentSigData.includes('.jpeg'))) {
                  return (
                    <SignatureImage src={parentSigData} alt="Parent/Guardian Signature" />
                  );
                }
                
                // Return empty if no valid signature data
                return <SigValue></SigValue>;
              })()}
            </SignatureRow>
            <ParentSignatureNote>(if under 20 years of age)</ParentSignatureNote>
          </SignatureSection>

        </FormContent>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default MediaReleaseForm; 